import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Fade,
} from '@material-ui/core';
import CallOutlinedIcon from '@material-ui/icons/CallOutlined';
import PhonelinkSetupIcon from '@material-ui/icons/PhonelinkSetup';
import { Alert } from '@material-ui/lab';
import { useDialogConfirm } from '@src/components/Common/Dialog/DialogProvider';
import useCallAgentPermissionFlags from '@src/hooks/call/useCallAgentPermissionFlags';
import useHasCallAccess from '@src/hooks/call/useHasCallAccess';
import useCallPermissionFlags from '@src/hooks/call/useCallPermissionFlags';
import usePreventMultipleClicks from '@src/hooks/usePreventMultipleClicks';
import useStartOutgoingContactCallMedia from '@src/logic/call/callMedia/hooks/useStartOutgoingContactCallMedia';
import { allBotAccounts } from '@src/reducers/allBotAccounts';
import { useLazyGetCallPermissionQuery } from '@src/reducers/callsAPI';
import { useStartChatMutation } from '@src/reducers/chatsApi';
import { allBotAccountsSelector } from '@src/selectors/allBotAccounts';
import {
  GetContactsResponseData,
  useLazyGetCurrentChatQuery,
} from '@src/reducers/contactsApi';
import { getUserId } from '@src/selectors/user';
import { ContactBotAccount, TemplatedMsgWrapper } from '@src/types';
import { ChatContact } from '@src/types/chats';
import getContactPhoneNumber from '@src/utils/contact';
import { removePhoneNumberPrefix } from '@src/utils/formatPhoneNumber';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import ContactCallPermissionsAlert from '../ContactCallPermissionsAlert';
import ContactBotAccountSelect from './ContactBotAccountSelect';
import ContactTemplatesSelect from './ContactTemplatesSelect';
import useStyles from './styles';
import StartContactCallFromBotDialog, {
  TakeChatFromCallStatus,
} from './StartContactCallFromBotDialog';

export interface StartContactCallDialogProps {
  contact: GetContactsResponseData;
  accountChecked: boolean | null;
  onClose: () => void;
}

const StartContactCallDialog: React.FC<StartContactCallDialogProps> = ({
  contact,
  accountChecked,
  onClose,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();
  const [startChat] = useStartChatMutation();
  const startOutgoingContactCall = useStartOutgoingContactCallMedia();
  const currentAgentId = useSelector(getUserId);
  const dialogConfirm = useDialogConfirm();

  const { isEnabled } = useCallAgentPermissionFlags();

  const [selectedBotAccount, setSelectedBotAccount] =
    useState<ContactBotAccount | null>(null);

  // The contact's messaging accounts don't carry the line's raw phone number
  // (`botAccount` is the channel username, e.g. "…@WHATSAPPB2CHAT"), so the
  // caller number is resolved from the merchant's bot accounts list.
  const merchantBotAccounts = useSelector(allBotAccountsSelector);

  useEffect(() => {
    if (!merchantBotAccounts?.length)
      dispatch(allBotAccounts.fetchAllBotAccounts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedLineIdentifier = useMemo(
    () =>
      merchantBotAccounts?.find(
        account => account.id === selectedBotAccount?.botAccountId
      )?.identifier,
    [merchantBotAccounts, selectedBotAccount]
  );

  const { hasCallAccess } = useHasCallAccess(selectedBotAccount?.botAccountId);

  const [selectedTemplate, setSelectedTemplate] =
    useState<TemplatedMsgWrapper | null>(null);

  const [forceToSelectATemplate] = useState(false);

  const [
    getPermissions,
    {
      data: permissions,
      isSuccess: isSucessPermissions,
      isError: isErrorPermissions,
    },
  ] = useLazyGetCallPermissionQuery();

  // Phone (preferred) or BSUID; null when the contact carries neither, in which
  // case we must not query permissions — a request without an identifier 400s
  // and would be misread as "no permission" (ENG-1715).
  const contactCallIdentifier = useMemo(() => {
    const contactPhone = removePhoneNumberPrefix(contact.mobileNumber || '');
    if (contactPhone) return { phoneNumber: contactPhone };
    if (contact.waUserId) return { contactBsuid: contact.waUserId };
    return null;
  }, [contact.mobileNumber, contact.waUserId]);

  const [
    getCurrentChat,
    {
      data: currentChat,
      isLoading: isCurrentChatLoading,
      isError: isCurrentChatError,
    },
  ] = useLazyGetCurrentChatQuery();

  useEffect(() => {
    if (selectedBotAccount) {
      // Only query permissions when the contact has an identifier — otherwise
      // the request 400s and would be misread as "no permission" (ENG-1715).
      if (contactCallIdentifier) {
        getPermissions(
          {
            phoneNumberId: selectedBotAccount?.botAccount || '',
            ...contactCallIdentifier,
          },
          false
        );
      }
      getCurrentChat(
        {
          contactId: `${contact.id}`,
          'bot-id': `${selectedBotAccount.botId}`,
          'via-bot-account': selectedBotAccount.botAccount || '',
          'via-msg-provider': selectedBotAccount.provider || '',
          'contact-account':
            getContactPhoneNumber(contact as unknown as ChatContact) || '',
        },
        false
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getPermissions,
    getCurrentChat,
    selectedBotAccount,
    contactCallIdentifier,
  ]);

  const [chatIsAlreadyTaken, setChatIsAlreadyTaken] = useState<boolean>(false);
  const [chatIsPickup, setChatIsPickup] = useState<boolean>(false);
  const [displayConfirmationDialog, setDisplayConfirmationDialog] =
    useState(false);

  const permissionFlags = useCallPermissionFlags(permissions);

  const onChangeSelectedBotAccount = useCallback(
    (botAccount: ContactBotAccount | null) => {
      setSelectedBotAccount(botAccount);
      setSelectedTemplate(null);
    },
    []
  );

  const onTemplateChange = useCallback(
    (template: TemplatedMsgWrapper | null) => {
      setSelectedTemplate(template);
    },
    []
  );

  const isStartCallDisabled = useMemo(
    () =>
      !selectedBotAccount ||
      permissionFlags.isStartCallExceeded ||
      isCurrentChatLoading,
    [selectedBotAccount, permissionFlags, isCurrentChatLoading]
  );

  const isSendCallTemplateDisabled = useMemo(
    () => !selectedTemplate || permissionFlags.isSendCallTemplateExceeded,
    [selectedTemplate, permissionFlags]
  );

  const onSendCallTemplate = async () => {
    if (!selectedBotAccount?.botAccount || !selectedTemplate?.templatedMsg?.id)
      return;
    const { data: updatedCurrentChat } = await getCurrentChat({
      contactId: `${contact.id}`,
      'bot-id': `${selectedBotAccount.botId}`,
      'via-bot-account': selectedBotAccount.botAccount || '',
      'via-msg-provider': selectedBotAccount.provider || '',
      'contact-account': selectedBotAccount.contactAccountId || '',
    });

    const fromNewTray =
      updatedCurrentChat?.status === TakeChatFromCallStatus.OPENED;

    try {
      await startChat({
        botId: selectedBotAccount.botId,
        viaBotAccount: selectedBotAccount.botAccount,
        viaMsgProvider: selectedBotAccount.provider,
        contactMsgAccount: selectedBotAccount.contactAccountId,
        contactId: `${contact.id}`,
        templatedMsgId: `${selectedTemplate?.templatedMsg.id}`,
        fromNewTray,
      }).unwrap();
    } catch (error) {
      dialogConfirm({
        title: formatMessage({
          id: 'Contact.startCallDialog.sendTemplateError.title',
        }),
        description: formatMessage({
          id: 'Contact.startCallDialog.sendTemplateError.description',
        }),
        type: 'error',
      });
    } finally {
      onClose();
    }
  };

  const onStartCall = async () => {
    if (!selectedBotAccount?.botAccount) return;
    const callePhoneNumber = removePhoneNumberPrefix(
      contact?.mobileNumber || ''
    );

    const callerPhoneNumber = removePhoneNumberPrefix(
      selectedLineIdentifier || ''
    );

    await startOutgoingContactCall({
      botId: selectedBotAccount.botId,
      viaBotAccount: selectedBotAccount.botAccount,
      callerPhoneNumber,
      contact: {
        id: contact.id,
        phoneNumber: callePhoneNumber,
        name: contact?.fullName || '',
        ...(contact.waUserId && { bsuid: contact.waUserId }),
      },
    });
    onClose();
  };

  const onStartCallOrSendTemplate = async () => {
    if (permissionFlags.isStartCallAllowed) onStartCall();
    else await onSendCallTemplate();
  };

  const onStartCallConfirmation = () => {
    if (
      currentChat?.status === TakeChatFromCallStatus.OPENED ||
      currentChat?.status === TakeChatFromCallStatus.BOT_CHATTING
    ) {
      setDisplayConfirmationDialog(true);
    } else {
      onSendConfirmationDialog();
    }
  };

  const safeOnStartCallConfirmation = usePreventMultipleClicks(
    onStartCallConfirmation,
    { blockTimeMs: 3000 }
  );

  const onSendConfirmationDialog = async () => {
    setDisplayConfirmationDialog(false);
    await onStartCallOrSendTemplate();
  };

  const safeOnSendConfirmationDialog = usePreventMultipleClicks(
    onSendConfirmationDialog,
    { blockTimeMs: 3000 }
  );

  useEffect(() => {
    if (isCurrentChatError) {
      setChatIsAlreadyTaken(false);
      setChatIsPickup(false);
      return;
    }
    if (
      currentChat &&
      currentChat.status === TakeChatFromCallStatus.PICKED_UP
    ) {
      setChatIsAlreadyTaken(currentChat?.assignedTo?.id !== currentAgentId);
      setChatIsPickup(currentChat?.assignedTo?.id === currentAgentId);
    }
  }, [currentChat, currentAgentId, isCurrentChatError]);

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        classes={{
          paper: classes.paper,
        }}
      >
        <DialogTitle>
          <Box className={classes.contactTitle}>
            <FormattedMessage id="Contact.startChatDialog.contact.label" />:{' '}
            {contact.fullName} | {contact.mobileNumber}
          </Box>
          {!permissionFlags.isStartCallAllowed ? (
            <Box className={classes.dialogSubtitle}>
              <FormattedMessage id="Contact.startCallDialog.subtitle.requestPermission" />
            </Box>
          ) : (
            <Box className={classes.dialogSubtitle}>
              <FormattedMessage id="Contact.startCallDialog.subtitle.needLine" />
            </Box>
          )}
        </DialogTitle>

        <DialogContent style={{ paddingTop: 0 }}>
          {!contactCallIdentifier && (
            <Box my={2}>
              <Alert severity="warning">
                <FormattedMessage id="Contact.startCallDialog.noCallNumber" />
              </Alert>
            </Box>
          )}

          {accountChecked && contactCallIdentifier && (
            <div>
              <Box my={2}>
                <ContactBotAccountSelect
                  value={selectedBotAccount}
                  contactAccountChecked={accountChecked}
                  onChange={onChangeSelectedBotAccount}
                />
              </Box>

              {selectedBotAccount && isErrorPermissions && (
                <Box mt={1}>
                  <Alert severity="error">
                    <FormattedMessage id="CallPermission.Error.ContentLbl" />
                  </Alert>
                </Box>
              )}

              <Fade in={isSucessPermissions}>
                <div>
                  {selectedBotAccount &&
                    (!permissionFlags.isStartCallAllowed ||
                      forceToSelectATemplate) && (
                      <ContactTemplatesSelect
                        templateContext={{
                          botAccountId: selectedBotAccount.botAccountId,
                          contactId: contact.id,
                        }}
                        onTemplateChange={onTemplateChange}
                      />
                    )}

                  {!accountChecked && (
                    <Alert severity="info">
                      <FormattedMessage id="Contact.startChatDialog.checkingAccounts" />
                    </Alert>
                  )}

                  <Box mt={2}>
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={safeOnStartCallConfirmation}
                      disabled={
                        (permissionFlags.isStartCallAllowed
                          ? isStartCallDisabled
                          : isSendCallTemplateDisabled) ||
                        isCurrentChatLoading ||
                        chatIsAlreadyTaken ||
                        chatIsPickup ||
                        !isEnabled ||
                        (!!selectedBotAccount && !hasCallAccess)
                      }
                      style={{ marginRight: '1.0em' }}
                      fullWidth
                    >
                      <Box display="flex" alignItems="center" gridGap={5}>
                        {permissionFlags.isStartCallAllowed ? (
                          <>
                            <CallOutlinedIcon />
                            <FormattedMessage id="Contact.startCallDialog.button.call" />
                          </>
                        ) : (
                          <>
                            <PhonelinkSetupIcon style={{ fontSize: 18 }} />
                            <FormattedMessage id="Contact.startCallDialog.button.requestPermission" />
                          </>
                        )}
                      </Box>
                    </Button>
                  </Box>

                  {permissions &&
                    (!chatIsAlreadyTaken ||
                      permissionFlags.isStartCallExceeded ||
                      !permissionFlags.isStartCallAllowed) && (
                      <Box mt={1}>
                        <ContactCallPermissionsAlert
                          contactPermissions={permissions}
                        />
                      </Box>
                    )}

                  {!isCurrentChatError &&
                    (chatIsAlreadyTaken || chatIsPickup) && (
                      <Box mt={1}>
                        <Alert severity="info">
                          <FormattedMessage
                            id="CallPermission.SendRequest.AlreadyTaken"
                            values={{
                              contactName: contact.fullName,
                              agentName: currentChat?.assignedTo?.fullName,
                            }}
                          />
                        </Alert>
                      </Box>
                    )}
                </div>
              </Fade>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {currentChat && !isCurrentChatError && (
        <StartContactCallFromBotDialog
          open={displayConfirmationDialog}
          onClose={() => {
            setDisplayConfirmationDialog(false);
            onClose();
          }}
          onConfirm={() => safeOnSendConfirmationDialog()}
          status={currentChat.status}
        />
      )}
    </>
  );
};

export default StartContactCallDialog;
